export const CORE = "urn:ietf:params:jmap:core";
export const MAIL = "urn:ietf:params:jmap:mail";
export const SUBMISSION = "urn:ietf:params:jmap:submission";

export type JMAPSession = {
  capabilities: Record<string, unknown>;
  accounts: Record<string, { name: string; isPersonal: boolean; isReadOnly: boolean }>;
  primaryAccounts: Record<string, string>;
  username: string;
  apiUrl: string;
  downloadUrl: string;
  uploadUrl: string;
  state: string;
};
export type JMAPCall = [string, Record<string, unknown>, string];
export type JMAPResponse = { methodResponses: Array<[string, unknown, string]>; createdIds?: Record<string, string>; sessionState: string };
export class JMAPError extends Error {
  type: string; invocation: string;
  constructor(type: string, message = type, invocation = "") { super(message); this.name = "JMAPError"; this.type = type; this.invocation = invocation; }
}
type Fetch = typeof fetch;
type ClientOptions = { fetch?: Fetch; csrf?: () => string; retries?: number; retryDelay?: number };

export class JMAPClient {
  private fetcher: Fetch;
  private sessionPromise?: Promise<JMAPSession>;
  private sequence = 0;
  readonly states = new Map<string, string>();
  private options: ClientOptions;
  constructor(options: ClientOptions = {}) { this.options = options; this.fetcher = options.fetch || globalThis.fetch.bind(globalThis); }
  session(refresh = false): Promise<JMAPSession> {
    if (refresh || !this.sessionPromise) this.sessionPromise = this.getSession();
    return this.sessionPromise;
  }
  private async getSession(): Promise<JMAPSession> {
    const response = await this.fetcher("/.well-known/jmap", { credentials: "include", headers: { Accept: "application/json" } });
    if (response.status === 401) throw new JMAPError("SESSION_EXPIRED");
    if (!response.ok) throw new JMAPError("serverUnavailable", "Mail service unavailable");
    return response.json() as Promise<JMAPSession>;
  }
  account(session: JMAPSession, capability = MAIL): string {
    const account = session.primaryAccounts[capability] || Object.keys(session.accounts)[0];
    if (!account) throw new JMAPError("accountNotFound", "Mail account unavailable");
    return account;
  }
  async call<T = unknown>(calls: JMAPCall[], using = [CORE, MAIL], signal?: AbortSignal): Promise<Array<[string, T, string]>> {
    const session = await this.session();
    const response = await this.post({ using, methodCalls: calls }, session.apiUrl, signal);
    for (const [name, value, invocation] of response.methodResponses) {
      if (name === "error") {
        const problem = value as { type?: string; description?: string };
        throw new JMAPError(problem.type || "serverFail", problem.description || "Mail operation failed", invocation);
      }
      const state = (value as { state?: string; newState?: string; queryState?: string }).newState || (value as { state?: string }).state || (value as { queryState?: string }).queryState;
      if (state) this.states.set(name.split("/")[0], state);
    }
    return response.methodResponses as Array<[string, T, string]>;
  }
  invocation(prefix = "c"): string { this.sequence += 1; return prefix + this.sequence; }
  async raw(url: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers);
    const method = (init.method || "GET").toUpperCase();
    const csrf = this.options.csrf?.();
    if (csrf && !["GET", "HEAD", "OPTIONS"].includes(method)) headers.set("X-CSRF-Token", csrf);
    const response = await this.fetcher(url, { ...init, headers, credentials: "include" });
    if (response.status === 401) { this.clearSession(); throw new JMAPError("SESSION_EXPIRED"); }
    return response;
  }
  resultReference(resultOf: string, name: string, path: string) { return { resultOf, name, path }; }
  clearSession() { this.sessionPromise = undefined; this.states.clear(); }
  watch(onChange: (event: { changed: Record<string,Record<string,string>> }) => void, onStatus?: (online:boolean)=>void): () => void {
    let stopped=false, socket:WebSocket|undefined, timer=0, attempts=0;
    const connect=async()=>{if(stopped)return;try{await this.session();const scheme=location.protocol==="https:"?"wss:":"ws:";socket=new WebSocket(scheme+"//"+location.host+"/jmap/ws?types=Email,Mailbox,Thread,EmailSubmission","jmap");
      socket.onopen=()=>{attempts=0;onStatus?.(true)};
      socket.onmessage=message=>{try{const event=JSON.parse(String(message.data));if(event["@type"]==="StateChange"&&event.changed){for(const states of Object.values(event.changed) as Array<Record<string,string>>)for(const [kind,state] of Object.entries(states))this.states.set(kind,state);onChange(event)}}catch{}};
      socket.onclose=()=>{onStatus?.(false);if(!stopped){attempts++;timer=window.setTimeout(()=>void connect(),Math.min(30000,500*Math.pow(2,attempts)))}};socket.onerror=()=>socket?.close();
    }catch{onStatus?.(false);if(!stopped)timer=window.setTimeout(()=>void connect(),5000)}};
    void connect();return()=>{stopped=true;window.clearTimeout(timer);socket?.close()};
  }
  private async post(body: unknown, url: string, signal?: AbortSignal): Promise<JMAPResponse> {
    const retries = Math.max(0, this.options.retries ?? 2);
    for (let attempt = 0; ; attempt++) {
      try {
        if (signal?.aborted) throw signal.reason || new DOMException("Aborted", "AbortError");
        const headers = new Headers({ "Content-Type": "application/json", Accept: "application/json" });
        const csrf = this.options.csrf?.();
        if (csrf) headers.set("X-CSRF-Token", csrf);
        const response = await this.fetcher(url, { method: "POST", credentials: "include", headers, body: JSON.stringify(body), signal });
        if (response.status === 401) { this.clearSession(); throw new JMAPError("SESSION_EXPIRED"); }
        const value = await response.json().catch(() => ({})) as JMAPResponse & { type?: string; detail?: string };
        if (!response.ok) throw new JMAPError(value.type || "serverFail", value.detail || "JMAP request failed");
        return value;
      } catch (error) {
        if (signal?.aborted || error instanceof JMAPError || attempt >= retries) throw error;
        await new Promise(resolve => setTimeout(resolve, (this.options.retryDelay ?? 100) * (attempt + 1)));
      }
    }
  }
}

export type JMAPMailbox = { id: string; name: string; role: string | null; totalEmails: number; unreadEmails: number };
export type JMAPAddress = { name: string; email: string };
export type JMAPBodyPart = { partId?: string; blobId?: string; size: number; name?: string; type: string; disposition?: string; subParts?: JMAPBodyPart[] };
export type JMAPEmail = {
  id: string; blobId: string; threadId: string; mailboxIds: Record<string, boolean>; keywords: Record<string, boolean>;
  size: number; receivedAt: string; sentAt: string; messageId: string[]; from: JMAPAddress[]; to: JMAPAddress[]; cc: JMAPAddress[]; bcc: JMAPAddress[];
  subject: string; preview: string; hasAttachment: boolean; bodyStructure: JMAPBodyPart; bodyValues: Record<string, { value: string }>;
};

export class JMAPMail {
  client: JMAPClient;
  constructor(client: JMAPClient) { this.client = client; }
  async mailboxes(signal?: AbortSignal): Promise<JMAPMailbox[]> {
    const session = await this.client.session(); const accountId = this.client.account(session);
    const id = this.client.invocation("m");
    const responses = await this.client.call<{ list: JMAPMailbox[] }>([["Mailbox/get", { accountId }, id]], [CORE, MAIL], signal);
    return responses[0][1].list || [];
  }
  async query(mailboxId = "", text = "", options: { unread?:boolean; flagged?:boolean; attachment?:boolean } = {}, signal?: AbortSignal): Promise<JMAPEmail[]> {
    const session = await this.client.session(); const accountId = this.client.account(session);
    const queryId = this.client.invocation("q"), getId = this.client.invocation("g");
    const filter: Record<string, unknown> = {};
    if (mailboxId) filter.inMailbox = mailboxId;
    if (text) filter.text = text;
    if (options.unread) filter.notKeyword = "$seen";
    if (options.flagged) filter.hasKeyword = "$flagged";
    if (options.attachment) filter.hasAttachment = true;
    const responses = await this.client.call<{ list?: JMAPEmail[] }>([
      ["Email/query", { accountId, filter, sort: [{ property: "receivedAt", isAscending: false }], limit: 100 }, queryId],
      ["Email/get", { accountId, "#ids": this.client.resultReference(queryId, "Email/query", "/ids") }, getId],
    ], [CORE, MAIL], signal);
    return responses[1][1].list || [];
  }
  async get(id: string, signal?: AbortSignal): Promise<JMAPEmail> {
    const session = await this.client.session(); const accountId = this.client.account(session);
    const responses = await this.client.call<{ list: JMAPEmail[] }>([["Email/get", { accountId, ids: [id] }, this.client.invocation("g")]], [CORE, MAIL], signal);
    const email = responses[0][1].list?.[0]; if (!email) throw new JMAPError("notFound", "Message not found");
    return email;
  }
  async update(id: string, patch: Record<string, unknown>, signal?: AbortSignal): Promise<void> {
    const session = await this.client.session(); const accountId = this.client.account(session);
    const responses = await this.client.call<{ notUpdated: Record<string, { type: string }> }>([["Email/set", { accountId, update: { [id]: patch } }, this.client.invocation("s")]], [CORE, MAIL], signal);
    const problem = responses[0][1].notUpdated?.[id]; if (problem) throw new JMAPError(problem.type);
  }
  async trash(id: string, signal?: AbortSignal): Promise<void> {
    const boxes = await this.mailboxes(signal); const trash = boxes.find(box => box.role === "trash");
    if (!trash) throw new JMAPError("notFound", "Trash mailbox is unavailable");
    await this.update(id, { mailboxIds: { [trash.id]: true } }, signal);
  }
  async upload(file: File, signal?: AbortSignal) {
    const session = await this.client.session(); const accountId = this.client.account(session);
    const url = session.uploadUrl.replace("{accountId}", encodeURIComponent(accountId));
    const response = await this.client.raw(url, { method: "POST", headers: { "Content-Type": file.type || "application/octet-stream", "X-JMAP-Filename": file.name }, body: file, signal });
    if (response.status === 401) throw new JMAPError("SESSION_EXPIRED");
    if (!response.ok) throw new JMAPError("uploadFailed", "Attachment upload failed");
    return response.json() as Promise<{ blobId: string; name: string; type: string; size: number }>;
  }
  async deleteUpload(blobId: string): Promise<void> {
    const session = await this.client.session(); const accountId = this.client.account(session);
    const response = await this.client.raw("/jmap/upload/" + encodeURIComponent(accountId) + "/" + encodeURIComponent(blobId), { method: "DELETE" });
    if (!response.ok && response.status !== 404) throw new JMAPError("uploadDeleteFailed", "Attachment removal failed");
  }
  async createDraft(message: { fromAddress:string; to:string; cc:string; bcc:string; subject:string; text:string; attachmentIds:string[] }, previousId = ""): Promise<string> {
    const session = await this.client.session(); const accountId = this.client.account(session);
    const boxes = await this.mailboxes(); const drafts = boxes.find(x => x.role === "drafts");
    if (!drafts) throw new JMAPError("notFound", "Drafts mailbox is unavailable");
    const split = (value:string) => value.split(/[,;]/).map(x=>x.trim()).filter(Boolean).map(email=>({email}));
    const key = this.client.invocation("draft");
    const create = { mailboxIds:{[drafts.id]:true}, keywords:{"$draft":true}, from:[{email:message.fromAddress}], to:split(message.to), cc:split(message.cc), bcc:split(message.bcc), subject:message.subject,
      bodyValues:{text:{value:message.text}}, textBody:[{partId:"text",type:"text/plain"}],
      attachments:message.attachmentIds.map(blobId=>({blobId,name:"attachment",type:"application/octet-stream",size:0})), receivedAt:new Date().toISOString() };
    const response = await this.client.call<{created:Record<string,{id:string}>;notCreated:Record<string,{type:string}>}>([["Email/set",{accountId,create:{[key]:create}},key]],[CORE,MAIL]);
    const result=response[0][1];if(result.notCreated?.[key])throw new JMAPError(result.notCreated[key].type);
    const id=result.created?.[key]?.id;if(!id)throw new JMAPError("serverFail","Draft was not created");
    if(previousId&&previousId!==id){try{await this.trash(previousId)}catch{}}
    return id;
  }
  async sendDraft(id:string, fromAddress:string):Promise<void>{
    const session=await this.client.session();const accountId=this.client.account(session,SUBMISSION);
    const identityCall=this.client.invocation("identity");
    const identities=await this.client.call<{list:Array<{id:string;email:string}>}>([["Identity/get",{accountId},identityCall]],[CORE,MAIL,SUBMISSION]);
    const identity=identities[0][1].list.find(x=>x.email.toLowerCase()===fromAddress.toLowerCase())||identities[0][1].list[0];
    if(!identity)throw new JMAPError("forbidden","No authorized sender identity");
    const key=this.client.invocation("submit");
    const response=await this.client.call<{created:Record<string,{id:string}>;notCreated:Record<string,{type:string}>}>([["EmailSubmission/set",{accountId,create:{[key]:{emailId:id,identityId:identity.id}},onSuccessDestroyEmail:["#"+key]},key]],[CORE,MAIL,SUBMISSION]);
    if(response[0][1].notCreated?.[key])throw new JMAPError(response[0][1].notCreated[key].type);
  }

}
